import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { SECTION_DEFS } from "../Home";
import { Input, Textarea, Toggle, ImageField, Btn, ListEditor, inputCls, labelCls } from "./AdminUI";
import { DESIGN_FONTS, DESIGN_RADII } from "../../components/DesignVars";
import { ChevronUp, ChevronDown, Save, Plus, X, Check } from "lucide-react";

const TABS = ["Homepage Text", "Sections", "About Page", "Design"];

export default function ContentAdmin() {
  const { reload } = useSettings();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("Homepage Text");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/content").then((r) => setC(r.data || {})); }, []);

  if (!c) return <div className="py-20 text-center text-muted">Loading…</div>;

  const set = (k, v) => { setC((p) => ({ ...p, [k]: v })); setSaved(false); };
  const secMap = c.sections || {};
  const setSection = (key, patch) => set("sections", { ...secMap, [key]: { ...(secMap[key] || {}), ...patch } });
  const ordered = SECTION_DEFS.map((d, i) => ({ ...d, order: secMap[d.key]?.order ?? i })).sort((a, b) => a.order - b.order);
  const move = (key, dir) => {
    const arr = [...ordered];
    const idx = arr.findIndex((x) => x.key === key);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    const m = { ...secMap };
    arr.forEach((x, i) => { m[x.key] = { ...(m[x.key] || {}), order: i }; });
    set("sections", m);
  };

  const stats = c.stats || [];
  const setStat = (i, k, v) => { const s = [...stats]; s[i] = { ...s[i], [k]: v }; set("stats", s); };
  const addStat = () => set("stats", [...stats, { icon: "Building2", value: "", label: "" }]);
  const removeStat = (i) => set("stats", stats.filter((_, k) => k !== i));

  const design = c.design || {};
  const setD = (k, v) => set("design", { ...design, [k]: v });
  const fontOpts = Object.keys(DESIGN_FONTS);

  const save = async () => {
    setSaving(true);
    try { await api.put("/content", c); await reload(); setSaved(true); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="content-admin">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-ink">Website Content</h1>
          <p className="text-sm text-muted">Edit homepage text, reorder sections and turn them on/off.</p>
        </div>
        <Btn onClick={save} data-testid="content-save" disabled={saving}>
          {saved ? <Check size={16} /> : <Save size={16} />} {saving ? "Saving…" : saved ? "Saved" : "Save Changes"}
        </Btn>
      </div>

      <div className="flex gap-2 mb-6 border-b border-line">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`content-tab-${t.split(" ")[0].toLowerCase()}`}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === t ? "border-gold text-gold" : "border-transparent text-muted hover:text-ink"}`}>
            {t}
          </button>
        ))}
      </div>

      {tab === "Homepage Text" && (
        <div className="space-y-6 max-w-3xl">
          <div className="rounded-lg border border-line p-5 bg-white space-y-4">
            <h3 className="font-bold text-ink text-sm">Hero Section</h3>
            <Input label="Kicker" value={c.hero_kicker} onChange={(v) => set("hero_kicker", v)} placeholder="We Design • We Build • We Deliver" />
            <div className="grid md:grid-cols-3 gap-3">
              <Input label="Heading line 1" value={c.hero_heading_1} onChange={(v) => set("hero_heading_1", v)} placeholder="Building the Future" />
              <Input label="Heading line 2 (gold)" value={c.hero_heading_2} onChange={(v) => set("hero_heading_2", v)} placeholder="with Smart Design &" />
              <Input label="Heading line 3" value={c.hero_heading_3} onChange={(v) => set("hero_heading_3", v)} placeholder="Quality Construction" />
            </div>
            <Textarea label="Subheading" value={c.hero_subheading} onChange={(v) => set("hero_subheading", v)} rows={2} align={c.hero_subheading_align} onAlign={(v) => set("hero_subheading_align", v)} />
            <div className="grid md:grid-cols-2 gap-3">
              <Input label="Primary Button" value={c.hero_cta} onChange={(v) => set("hero_cta", v)} placeholder="Get Free Consultation" />
              <Input label="Secondary Button" value={c.hero_cta_secondary} onChange={(v) => set("hero_cta_secondary", v)} placeholder="View Our Projects" />
            </div>
            <ImageField label="Hero Image" value={c.hero_image} onChange={(v) => set("hero_image", v)} folder="gnons/hero/" />
          </div>

          <div className="rounded-lg border border-line p-5 bg-white">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-ink text-sm">Statistics</h3>
              <Btn variant="outline" onClick={addStat} data-testid="add-stat"><Plus size={14} /> Add Stat</Btn>
            </div>
            <p className="text-xs text-muted mb-3">Icon uses a Lucide icon name (e.g. Building2, CalendarClock, Smile, Users).</p>
            <div className="space-y-2">
              {stats.map((s, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_2fr_auto] gap-2 items-center" data-testid={`stat-row-${i}`}>
                  <input className={inputCls} placeholder="Icon" value={s.icon || ""} onChange={(e) => setStat(i, "icon", e.target.value)} />
                  <input className={inputCls} placeholder="Value (e.g. 500+)" value={s.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} />
                  <input className={inputCls} placeholder="Label" value={s.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} />
                  <button onClick={() => removeStat(i)} className="text-red-500 p-1"><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "Sections" && (
        <div className="space-y-3 max-w-3xl" data-testid="sections-list">
          <p className="text-sm text-muted">Drag order with the arrows, toggle a section on/off, and override its heading/subtitle. Changes apply to the homepage after saving.</p>
          {ordered.map((d, i) => {
            const cfg = secMap[d.key] || {};
            const enabled = cfg.enabled !== false;
            const hasHeading = !!d.heading;
            return (
              <div key={d.key} className={`rounded-lg border p-4 ${enabled ? "border-line bg-white" : "border-line bg-sand opacity-70"}`} data-testid={`section-${d.key}`}>
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <button onClick={() => move(d.key, -1)} disabled={i === 0} className="text-muted hover:text-ink disabled:opacity-30" data-testid={`section-up-${d.key}`}><ChevronUp size={16} /></button>
                    <button onClick={() => move(d.key, 1)} disabled={i === ordered.length - 1} className="text-muted hover:text-ink disabled:opacity-30" data-testid={`section-down-${d.key}`}><ChevronDown size={16} /></button>
                  </div>
                  <div className="flex-1">
                    <div className="font-semibold text-ink">{d.label}</div>
                    <div className="text-[11px] text-muted">Position {i + 1}</div>
                  </div>
                  <Toggle label={enabled ? "On" : "Off"} checked={enabled} onChange={(v) => setSection(d.key, { enabled: v })} />
                </div>
                {hasHeading && enabled && (
                  <div className="grid md:grid-cols-2 gap-3 mt-4 pl-8">
                    <Input label="Heading override" value={cfg.heading} onChange={(v) => setSection(d.key, { heading: v })} placeholder={d.heading} />
                    <Input label="Subtitle override" value={cfg.subtitle} onChange={(v) => setSection(d.key, { subtitle: v })} placeholder={d.subtitle || "—"} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "About Page" && (
        <div className="space-y-6 max-w-3xl" data-testid="about-editor">
          <div className="rounded-lg border border-line p-5 bg-white space-y-4">
            <h3 className="font-bold text-ink text-sm">About Content</h3>
            <Textarea label="Company Introduction" value={c.about_intro} onChange={(v) => set("about_intro", v)} rows={4} align={c.about_intro_align} onAlign={(v) => set("about_intro_align", v)} />
            <div className="grid md:grid-cols-2 gap-4">
              <Textarea label="Mission" value={c.mission} onChange={(v) => set("mission", v)} rows={4} align={c.mission_align} onAlign={(v) => set("mission_align", v)} />
              <Textarea label="Vision" value={c.vision} onChange={(v) => set("vision", v)} rows={4} align={c.vision_align} onAlign={(v) => set("vision_align", v)} />
            </div>
            <ListEditor label="Core Values" items={c.core_values || []} onChange={(v) => set("core_values", v)} placeholder="Add a core value" />
          </div>
        </div>
      )}

      {tab === "Design" && (
        <div className="space-y-6 max-w-2xl" data-testid="design-editor">
          <div className="rounded-lg border border-line p-5 bg-white space-y-5">
            <h3 className="font-bold text-ink text-sm">Global Design</h3>
            <p className="text-xs text-muted">Safe, site-wide presets. Changes apply everywhere after saving — layout and responsiveness stay intact.</p>
            <div>
              <label className={labelCls}>Accent Color (gold)</label>
              <div className="flex items-center gap-3">
                <input type="color" value={design.accent || "#c88a32"} onChange={(e) => setD("accent", e.target.value)} className="w-12 h-10 rounded border border-line cursor-pointer" data-testid="design-accent" />
                <input className={inputCls + " w-40"} value={design.accent || "#c88a32"} onChange={(e) => setD("accent", e.target.value)} />
                <button onClick={() => setD("accent", "#c88a32")} className="text-xs text-muted underline">Reset default</button>
              </div>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Heading Font</label>
                <select className={inputCls} value={design.heading_font || "Manrope"} onChange={(e) => setD("heading_font", e.target.value)} data-testid="design-heading-font">
                  {fontOpts.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
              <div>
                <label className={labelCls}>Body Font</label>
                <select className={inputCls} value={design.body_font || "Manrope"} onChange={(e) => setD("body_font", e.target.value)} data-testid="design-body-font">
                  {fontOpts.map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Corner Radius</label>
              <select className={inputCls} value={design.radius || "1.25rem"} onChange={(e) => setD("radius", e.target.value)} data-testid="design-radius">
                {Object.entries(DESIGN_RADII).map(([name, val]) => <option key={val} value={val}>{name}</option>)}
              </select>
            </div>
            <div className="rounded-lg bg-sand border border-line p-4">
              <div className="flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center px-4 py-2 rounded-xl2 bg-gold text-white text-sm font-semibold">Preview Button</span>
                <span className="text-gold font-bold" style={{ fontFamily: `"${design.heading_font || "Manrope"}", sans-serif` }}>Accent Heading Text</span>
              </div>
              <p className="text-xs text-muted mt-2">Live preview reflects saved changes across the whole site.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
